import React from 'react';
import { graphql } from 'gatsby';
import {
  ContentfulProgramsPage,
  ContentfulHeadingTextImage,
} from '../graphql-types';
import SEO from '../components/SEO';
import Layout from '../components/Layout';
import Header, { HeaderType, TextHeader } from '../components/Product/Header';
import Steps from '../components/Product/Steps';
import Features from '../components/Product/Features';
import Programs from '../components/Product/Programs';
import PersonalizedContent from '../components/Product/PersonalizedContent';
import FAQ from '../components/Product/FAQ';
import { useIntl } from 'react-intl';
import CaraCareVideo from '../assets/svgs/product/img-video_thumb-linda.png';
import VideoOverlay from '../components/VideoOverlay';
import { typeformEligiblityCheckerLink } from '../utils/constants';
import Knowledge from '../assets/svgs/product/img-module-knowledge.svg';
import Emotions from '../assets/svgs/product/img-module-emotions.svg';
import Nutrition from '../assets/svgs/product/img-module-nutrition.svg';
import Hypnosis from '../assets/svgs/product/img-module-hypnosis.svg';
import PhoneImage1 from '../assets/svgs/product/img-tracking.svg';
import PhoneImage2 from '../assets/svgs/product/img-program.svg';
import PhoneImage3 from '../assets/svgs/product/img-recipes.svg';
import PhoneImage4 from '../assets/svgs/product/img-chat.svg';
import BgImage1 from '../assets/svgs/product/img-bg-slideshow-features-1.svg';
import BgImage2 from '../assets/svgs/product/img-bg-slideshow-features-2.svg';
import BgImage3 from '../assets/svgs/product/img-bg-slideshow-features-3.svg';
import BgImage4 from '../assets/svgs/product/img-bg-slideshow-features-4.svg';
import Rundown1 from '../assets/svgs/product/img-program-rundown-1.svg';
import Rundown2 from '../assets/svgs/product/img-program-rundown-2.svg';
import Rundown3 from '../assets/svgs/product/img-program-rundown-3.svg';
import Rundown4 from '../assets/svgs/product/img-program-rundown-4.svg';

interface Props {
  pathContext: {
    locale: 'en' | 'de';
  };
  data: {
    programs: ContentfulProgramsPage;
    benefitsBoxes: { nodes: [{ benefitsBoxes: ContentfulHeadingTextImage[] }] };
  };
}

const Product: React.FC<Props> = ({
  pathContext: { locale },
  data: { programs },
}) => {
  const intl = useIntl();

  // Contentful alternative - temporary placement until we replace contentful or find a better solution
  const content: TextHeader = {
    type: HeaderType.Text,
    prefix: intl.formatMessage({
      id: 'product.page.header.prefix',
      defaultMessage: 'Verschreibung, Funktionen & Inhalte',
    }),
    title: intl.formatMessage({
      id: 'product.page.header.title',
      defaultMessage:
        'Linda erklärt euch<br/> <span style="color: #489F9D; fontWeight: 700;">Cara Care für Reizdarm</span>',
    }),
    text: intl.formatMessage({
      id: 'product.page.header.text',
      defaultMessage:
        'Wir helfen dir Schritt für Schritt zur signifikanten Reduktion deiner Symptome und verbesserter Lebensqualität.',
    }),
    buttonText: intl.formatMessage({
      id: 'product.page.header.cta.text',
      defaultMessage: 'Jetzt Eignung testen',
    }),
    buttonUrl: typeformEligiblityCheckerLink,
    rightElement: (
      <VideoOverlay
        buttonType="image"
        link="https://www.youtube.com/embed/DCofRWr6jaM"
        thumbnail={CaraCareVideo}
      />
    ),
    rightElementType: 'video',
  };

  const dataObj = [
    {
      prefix: intl.formatMessage({
        id: 'product.page.personalised.content.item1.prefix',
        defaultMessage: 'Pflichtmodul:',
      }),
      title: intl.formatMessage({
        id: 'product.page.personalised.content.item1.title',
        defaultMessage: 'Basiswissen',
      }),
      description: intl.formatMessage({
        id: 'product.page.personalised.content.item1.text',
        defaultMessage:
          'Werde zur Expertin bzw. zum Experten des Reizdarmsyndroms und lege so die Basis für deine erfolgreiche Therapie.\n\nLerne mehr über die Ursachen, die Entstehung, die Diagnostik sowie Therapiemöglichkeiten des Reizdarmsyndroms.',
      }),
      color: '#3D72DF26',
      src: Knowledge,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.personalised.content.item2.prefix',
        defaultMessage: 'optionales Modul:',
      }),
      title: intl.formatMessage({
        id: 'product.page.personalised.content.item2.title',
        defaultMessage: 'Gefühle & Verhalten',
      }),
      description: intl.formatMessage({
        id: 'product.page.personalised.content.item2.text',
        defaultMessage:
          'Entwickle Strategien, um den negativen Einfluss hinderlicher Gedanken, Gefühle und Verhaltensweisen auf deine Psyche und dein körperliches Wohlbefinden zu reduzieren.\n\nIm Rahmen des Moduls werden dir zusätzlich ein Gedanken- und Schlaftagebuch sowie angeleitete Entspannungsübungen zur Verfügung gestellt.',
      }),
      color: '#FFAFA433',
      src: Emotions,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.personalised.content.item3.prefix',
        defaultMessage: 'optionales Modul:',
      }),
      title: intl.formatMessage({
        id: 'product.page.personalised.content.item3.title',
        defaultMessage: 'Ernährung',
      }),
      description: intl.formatMessage({
        id: 'product.page.personalised.content.item3.text',
        defaultMessage:
          'Identifiziere im Rahmen der Low-FODMAP-Diät deine persönlichen Symptomauslöser und finde Schritt für Schritt eine verträgliche Dauerernährung.\n\nZusätzlich erhältst du personalisierte Empfehlungen zum Umgang mit bestehenden Allergien und Intoleranzen sowie eine auf dich angepasste Lebensmittelliste.',
      }),
      color: '#F5D17640',
      src: Nutrition,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.personalised.content.item4.prefix',
        defaultMessage: 'optionales Modul:',
      }),
      title: intl.formatMessage({
        id: 'product.page.personalised.content.item4.title',
        defaultMessage: 'Audio-geführte Hypnose',
      }),
      description: intl.formatMessage({
        id: 'product.page.personalised.content.item3.text',
        defaultMessage:
          'Ziel der darmgerichteten Hypnose ist es die Darm-Hirn-Achse durch Suggestionen positiv zu beeinflussen und so deine Beschwerden durch Entspannung zu lindern.\n\nLerne die Grundlagen der Hypnose kennen, werde durch Übungen unterstützt und führe regelmäßig audio-geführte Hypnosen durch.',
      }),
      color: '#9775C126',
      src: Hypnosis,
    },
  ];

  const featuresData = [
    {
      prefix: intl.formatMessage({
        id: 'product.page.features.feature1.prefix',
        defaultMessage: 'Tracking & Tagebuch',
      }),
      title: intl.formatMessage({
        id: 'product.page.features.feature1.title',
        defaultMessage: 'Erfasse deine Ernährung, Symptome, und Befinden.',
      }),
      description: intl.formatMessage({
        id: 'product.page.features.feature1.text',
        defaultMessage:
          'Identifiziere mit Hilfe der Wochen- und Monatsübersicht unseres Tagebuchs die Zusammenhänge und Triggerfaktoren deiner Symptome.',
      }),
      phoneImage: PhoneImage1,
      bgImage: BgImage1,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.features.feature2.prefix',
        defaultMessage: 'Individuelle Unterstützung',
      }),
      title: intl.formatMessage({
        id: 'product.page.features.feature2.title',
        defaultMessage: 'Ein Program, das speziell auf dich zugeschnitten ist.',
      }),
      description: intl.formatMessage({
        id: 'product.page.features.feature1.text',
        defaultMessage:
          'Mit Hilfe unserer medizinischen Fragebögen stellen wir dir aus verschiedenen Modulen ein individualisiertes Programm zusammen, das genau auf deine Bedürfnisse ausgerichtet ist.',
      }),
      phoneImage: PhoneImage2,
      bgImage: BgImage2,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.features.feature3.prefix',
        defaultMessage: 'Rezepte',
      }),
      title: intl.formatMessage({
        id: 'product.page.features.feature3.title',
        defaultMessage: 'Reizdarmfreundliche  Gerichte für jeden Anlass.',
      }),
      description: intl.formatMessage({
        id: 'product.page.features.feature3.text',
        defaultMessage:
          'Egal, ob Frühstück, Mittagessen, oder Snacks — Lass dich von unseren reizdarmfreundlichen Rezepten inspirieren. Dabei filtern wir auch nach deinen Allergien und Unverträglichkeiten.',
      }),
      phoneImage: PhoneImage3,
      bgImage: BgImage3,
    },
    {
      prefix: intl.formatMessage({
        id: 'product.page.features.feature4.prefix',
        defaultMessage: 'Technischer Kundensupport',
      }),
      title: intl.formatMessage({
        id: 'product.page.features.feature4.title',
        defaultMessage: 'Unklarheiten? Wir sind für dich da!',
      }),
      description: intl.formatMessage({
        id: 'product.page.features.feature4.text',
        defaultMessage:
          'Unser technischer In-App Kundensupport greift dir bei Fragen und Problemen jederzeit helfend unter die Arme, um dir eine störungsfreie Therapie zu ermöglichen.',
      }),
      phoneImage: PhoneImage4,
      bgImage: BgImage4,
    },
  ];

  const programData = [
    {
      src: Rundown1,
      prefix: intl.formatMessage({
        id: 'product.page.programs.item1.prefix',
        defaultMessage: 'Vor Programmstart',
      }),
      title: intl.formatMessage({
        id: 'product.page.programs.item1.title',
        defaultMessage: 'Vorbereitungsphase & Gesundheits-Fragebogen',
      }),
      description: intl.formatMessage({
        id: 'product.page.programs.item1.text',
        defaultMessage:
          'Zu Beginn des Programms stehen dir in der sogenannten Vorbereitungsphase einführende Artikel rund um das Programm sowie ein ausführlicher Gesundheits-Fragebogen zur Verfügung.\n\n Der Gesundheits-Fragebogen bildet den Grundstein für dein personalisiertes Programm: Basierend auf deinen Angaben im Fragebogen wird in der nächsten Phase die für dich passende Modulkombination aus unterschiedlichen Therapiebereichen zusammengestellt.',
      }),
    },
    {
      src: Rundown2,
      prefix: intl.formatMessage({
        id: 'product.page.programs.item2.prefix',
        defaultMessage: '1. - 12. Woche',
      }),
      title: intl.formatMessage({
        id: 'product.page.programs.item2.title',
        defaultMessage:
          'Personalisierte Therapiephase durch optimierte Modulwahl',
      }),
      description: intl.formatMessage({
        id: 'product.page.programs.item2.text',
        defaultMessage:
          'Neben dem Modul Basiswissen wirst du abhängig von deinen Angaben im Gesundheits-Fragebogen zusätzlich durch die Low-FODMAP-Diät, durch ein Selbsthilfe-Manual aus der kognitiven Verhaltenstherapie oder durch audio-geführte Hypnose-Einheiten geleitet. Dabei können je nach Angaben 1 - 3 Module gleichzeitig freigeschaltet werden.',
      }),
    },
    {
      src: Rundown3,
      prefix: intl.formatMessage({
        id: 'product.page.programs.item3.prefix',
        defaultMessage: 'alle 4 Wochen',
      }),
      title: intl.formatMessage({
        id: 'product.page.programs.item3.title',
        defaultMessage: 'Regelmäßige Verlaufskontrolle über Folge-Fragebogen',
      }),
      description: intl.formatMessage({
        id: 'product.page.programs.item3.text',
        defaultMessage:
          'Zusätzlich erfolgt alle 4 Wochen eine optionale aber ausdrücklich empfohlene Verlaufskontrolle deines Behandlungsfortschritts statt. Der Folge-Fragebogen ist kürzer als der Gesundheits-Fragebogen, den du zu Beginn ausfüllst. Hier wird vor allem nach der Entwicklung deiner Beschwerden und deiner Lebensqualität gefragt.\n\nOb du dich verbessert hast, siehst du auf einen Blick in der App in Form einer graphischen Auswertung. Außerdem kannst du deinen persönlichen Fortschrittsbericht exportieren.',
      }),
    },
    {
      src: Rundown4,
      prefix: intl.formatMessage({
        id: 'product.page.programs.item4.prefix',
        defaultMessage: 'nach 12 Wochen',
      }),
      title: intl.formatMessage({
        id: 'product.page.programs.item4.title',
        defaultMessage: 'Ende des 12-Wochen-Programmzyklus',
      }),
      description: intl.formatMessage({
        id: 'product.page.programs.item4.text',
        defaultMessage:
          'Wenn du im zeitlich empfohlenen Rahmen unseres Programms geblieben bist, hast du alle Inhalte nach 12 Wochen durchlaufen, und dir geht es hoffentlich schon viel besser.\n\nDu kannst den Nutzungszeitraum jederzeit per Folgeverschreibung verlängern, um so weiter von den bereitgestellten Inhalten, den Rezepten, den Tagebuch- & Trackingfunktionen, und mehr zu profitieren und deine neuen Lebensgewohnheiten zu festigen.',
      }),
    },
  ];

  return (
    <Layout footerType="prescription">
      <SEO lang={locale} title={programs.seoTitle} />
      <Header content={content} />
      <Steps />
      <Features dataObj={null} />
      <Programs dataObj={programData} />
      <PersonalizedContent dataObj={dataObj} />
      <FAQ />
    </Layout>
  );
};

export const aboutPageQuery = graphql`
  query programsPage($locale: String!) {
    programs: contentfulProgramsPage(node_locale: { eq: $locale }) {
      seoTitle
      heroTitle
      heroText {
        childMarkdownRemark {
          rawMarkdownBody
          html
        }
      }
      chatBubbles {
        id
        heading
        shortText
        image {
          fixed(width: 40) {
            ...GatsbyContentfulFixed_withWebp
          }
        }
      }
      video {
        url
        text
        isDisplayed
      }
      insurancesHeadline
      logos {
        id
        image {
          title
          fixed(width: 120) {
            ...GatsbyContentfulFixed_withWebp
          }
        }
        isDisplayed
      }
      comparison {
        prefix
        title
        text {
          childMarkdownRemark {
            rawMarkdownBody
          }
        }
      }

      comparisonPrograms
      comparisonList {
        name
        description
        tracking
        programs
      }
      comparisonFooter {
        currency
        description
        callToAction {
          text
          url
          isDisplayed
        }
        policy
        policyDescription
      }
      isTeamSectionDisplayed
      scienceSection {
        prefix
        title
        text {
          childMarkdownRemark {
            rawMarkdownBody
          }
        }
        callToAction {
          text
          url
          isDisplayed
        }
      }
      testimonialCta {
        text
        url
        isDisplayed
      }
      sliderSection {
        prefix
        text {
          childMarkdownRemark {
            rawMarkdownBody
          }
        }
        image {
          fluid: fluid(maxWidth: 500) {
            ...GatsbyContentfulFluid
          }
        }
      }
    }
    #
    benefitsBoxes: allContentfulProgramsPage(
      filter: { node_locale: { eq: $locale } }
    ) {
      nodes {
        benefitsBoxes {
          heading
          text {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          image {
            fluid: fluid(maxWidth: 638) {
              ...GatsbyContentfulFluid
            }
          }
        }
      }
    }
  }
`;

export default Product;
